import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const interestOptions = [
  { value: "private-office", label: "Private Office" },
  { value: "coworking", label: "Coworking / Hot Desk" },
  { value: "conference-room", label: "Conference Room Rental" },
  { value: "day-pass", label: "Day Pass" },
  { value: "other", label: "Other / General Inquiry" },
];

export const TourRequestForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (value) => {
    setFormData((prev) => ({ ...prev, interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.interest) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    
    try {
      await axios.post(`${API}/tour-request`, formData);
      toast.success("Thank you! We'll contact you shortly to schedule your tour.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        interest: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting tour request:", error);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="tour-request-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="name" className="font-sans text-sm font-medium text-brand">
            Full Name *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            className="border-sand focus:border-sea focus:ring-sea"
            data-testid="tour-form-name"
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email" className="font-sans text-sm font-medium text-brand">
            Email Address *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            className="border-sand focus:border-sea focus:ring-sea"
            data-testid="tour-form-email"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="phone" className="font-sans text-sm font-medium text-brand">
            Phone Number *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(321) 555-0000"
            className="border-sand focus:border-sea focus:ring-sea"
            data-testid="tour-form-phone"
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="interest" className="font-sans text-sm font-medium text-brand">
            I'm Interested In *
          </Label>
          <Select value={formData.interest} onValueChange={handleInterestChange}>
            <SelectTrigger 
              className="border-sand focus:border-sea focus:ring-sea"
              data-testid="tour-form-interest"
            >
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {interestOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="font-sans text-sm font-medium text-brand">
          Message (Optional)
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your workspace needs..."
          className="border-sand focus:border-sea focus:ring-sea min-h-[120px]"
          data-testid="tour-form-message"
        />
      </div>

      <Button
        type="submit"
        disabled={isLoading}
        className="w-full sm:w-auto bg-brand text-white hover:bg-brand-light rounded-full px-8 py-3 btn-primary"
        data-testid="tour-form-submit"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          "Request a Tour"
        )}
      </Button>
    </form>
  );
};

export default TourRequestForm;
